import { HelpOutline } from '@mui/icons-material'
import {
  FormControl,
  FormHelperText,
  FormLabel,
  Stack,
  SxProps,
  TextField,
  Theme,
  Tooltip,
  useTheme,
} from '@mui/material'
import { FieldProps } from 'formik'
import {
  ChangeEvent,
  ComponentProps,
  PropsWithChildren,
  ReactNode,
} from 'react'

import { TPropsWithFieldProps } from '~/components/formikFields/models'
import WrapWithFastField from '~/components/formikFields/wrapWithFastField'
import { ECapability } from '~/features/user/models'
import useCapabilities from '~/hooks/useCapabilities'
import rem from '~/utils/ui'

const getInputStyles = ({
  multiline = false,
  withTextFieldLabel = false,
}: {
  multiline?: boolean
  withTextFieldLabel?: boolean
}) => {
  if (withTextFieldLabel) {
    return {}
  }

  return {
    '.MuiInputBase-input': {
      paddingBlock: 0,
    },
    '.MuiOutlinedInput-root': {
      borderRadius: rem(8),
      height: multiline ? undefined : rem(36),
      ...(multiline ? { paddingBlock: rem(6.5) } : {}),
    },
  } as SxProps<Theme>
}

function LabelInputWrapper({
  label,
  required,
  rightLabelContent = null,
  tooltipTitle = '',
}: {
  label: ReactNode
  required: boolean
  rightLabelContent?: ReactNode
  tooltipTitle?: string
}) {
  const theme = useTheme()

  if (!label) {
    return null
  }

  if (tooltipTitle) {
    return (
      <Stack direction="row" alignItems="center" spacing={1}>
        <FormLabel
          required={required}
          sx={{
            '&, &.Mui-focused': {
              color: theme.palette.mainFont,
              fontSize: rem(14),
              fontWeight: 500,
            },
          }}
        >
          {label}
        </FormLabel>

        <Tooltip title={tooltipTitle}>
          <HelpOutline
            sx={{
              fontSize: rem(16),
              color: theme.palette.tertiary4,
            }}
          />
        </Tooltip>
        {rightLabelContent}
      </Stack>
    )
  }

  if (rightLabelContent) {
    return (
      <Stack direction="row" alignItems="center" spacing={1}>
        <FormLabel
          required={required}
          sx={{
            '&, &.Mui-focused': {
              color: theme.palette.mainFont,
              fontSize: rem(14),
              fontWeight: 500,
            },
          }}
        >
          {label}
        </FormLabel>
        {rightLabelContent}
      </Stack>
    )
  }

  return (
    <FormLabel
      required={required}
      sx={{
        '&, &.Mui-focused': {
          color: theme.palette.mainFont,
          fontSize: rem(14),
          fontWeight: 500,
        },
      }}
    >
      {label}
    </FormLabel>
  )
}

function InputWrapper({
  children = null,
  helperText = null,
  rightContent = null,
}: PropsWithChildren<{ helperText?: ReactNode; rightContent?: ReactNode }>) {
  if (!rightContent) {
    return children
  }

  return (
    <Stack>
      <Stack direction="row" alignItems="center" spacing={1}>
        {children}
        {rightContent}
      </Stack>
      <FormHelperText>{helperText}</FormHelperText>
    </Stack>
  )
}

type TProps = Omit<
  ComponentProps<typeof TextField>,
  'error' | 'name' | 'onChange' | 'value' | 'variant'
> &
  TPropsWithFieldProps<string, object> & {
    capability?: ECapability
    getDisplayValue?: (value: string) => string
    rightContent?: ReactNode
    rightLabelContent?: ReactNode
    tooltipTitle?: string
    withTextFieldLabel?: boolean
    onChange?: (
      event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      fieldProps: FieldProps<string, object>,
    ) => void | Promise<void>
    hasPromptBankExamples?: boolean
  }

const FormikLabeledTextField = WrapWithFastField<TProps, string, object>(
  function Component({
    fieldProps,
    getDisplayValue,
    helperText: helperTextProps,
    label,
    multiline = false,
    onChange: onChangeProps,
    rightContent = null,
    rightLabelContent = null,
    required = false,
    sx = {},
    tooltipTitle,
    withTextFieldLabel = false,
    capability,
    FormHelperTextProps,
    hasPromptBankExamples = false,
    ...restProps
  }) {
    const { canEdit } = useCapabilities()

    const {
      field: { onChange, ...field },
      form,
      meta,
    } = fieldProps
    const { isSubmitting } = form
    const { error, touched } = meta

    let helperText: string | ReactNode =
      touched && error ? error : helperTextProps || ''
    const value = getDisplayValue ? getDisplayValue(field.value) : field.value

    if (hasPromptBankExamples) {
      helperText = helperTextProps
    }

    const isDisabledByCapability = capability && !canEdit(capability)
    const isDisabled =
      !!restProps.disabled || isSubmitting || isDisabledByCapability

    const handleChange = (
      event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      onChange(event)
      onChangeProps?.(event, fieldProps)
    }

    return (
      <FormControl
        error={!!touched && !!error}
        sx={{ gap: rem(8), width: '100%', ...sx }}
      >
        {!withTextFieldLabel && (
          <LabelInputWrapper
            label={label}
            required={required}
            rightLabelContent={rightLabelContent}
            tooltipTitle={tooltipTitle}
          />
        )}
        <InputWrapper helperText={helperText} rightContent={rightContent}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            {...restProps}
            {...field}
            disabled={isDisabled}
            error={!!touched && !!error}
            helperText={rightContent ? undefined : helperText}
            FormHelperTextProps={FormHelperTextProps}
            label={withTextFieldLabel ? label : undefined}
            multiline={multiline}
            required={withTextFieldLabel ? required : undefined}
            sx={getInputStyles({ multiline, withTextFieldLabel })}
            value={value}
            onChange={handleChange}
          />
        </InputWrapper>
      </FormControl>
    )
  },
)

export default FormikLabeledTextField
